import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'fyo_admin_ab_pwd';

const CARD_BG = 'linear-gradient(165deg, #1a2820 0%, #131e16 100%)';
const GOLD = '#C5A24A';
const GOLD_LT = '#e6c96e';
const IVORY = '#f2ede0';
const MUTED = 'rgba(242,237,224,0.55)';
const FAINT = 'rgba(242,237,224,0.30)';
const LINE = 'rgba(197,162,74,0.24)';
const INK = '#070d09';

const font = { display: "'Cormorant Garamond', Georgia, serif", body: "'DM Sans', system-ui, sans-serif" };

function StatCard({ label, value, sub, delta }) {
  const deltaColor = delta > 0 ? '#9be3aa' : delta < 0 ? '#ff9b8c' : MUTED;
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${LINE}`, borderRadius: 12, padding: '22px 24px', flex: 1, minWidth: 180, boxShadow: '0 18px 44px rgba(0,0,0,0.35)' }}>
      <div style={{ fontFamily: font.body, fontWeight: 600, fontSize: 10, letterSpacing: '0.28em', textTransform: 'uppercase', color: GOLD, marginBottom: 10 }}>{label}</div>
      <div style={{ fontFamily: font.display, fontWeight: 600, fontSize: 40, color: IVORY, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontFamily: font.body, fontWeight: 400, fontSize: 12, color: MUTED, marginTop: 8 }}>{sub}</div>}
      {typeof delta === 'number' && Number.isFinite(delta) && (
        <div style={{ fontFamily: font.body, fontWeight: 600, fontSize: 12, color: deltaColor, marginTop: 6 }}>
          {delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} {Math.abs(delta)}pp vs v1
        </div>
      )}
    </div>
  );
}

function VersionColumn({ title, subtitle, data, deltaVs }) {
  const rows = [
    { label: 'Opt-ins',        val: data.optins },
    { label: 'Completions',    val: data.completions },
    { label: 'Completion rate', val: data.completion_rate == null ? 'n/a' : `${data.completion_rate}%`, delta: deltaVs?.completion_rate },
    { label: 'Hot leads (Q7)', val: `${data.hot} · ${data.hot_pct}%`, delta: deltaVs?.hot_pct },
    { label: 'Warm leads',     val: `${data.warm} · ${data.warm_pct}%` },
    { label: 'Cold leads',     val: `${data.cold} · ${data.cold_pct}%` },
  ];
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${LINE}`, borderRadius: 14, padding: '28px 28px 26px', flex: 1, minWidth: 280, position: 'relative', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(to right, transparent 5%, ${GOLD_LT} 30%, ${GOLD} 50%, ${GOLD_LT} 70%, transparent 95%)`, borderTopLeftRadius: 14, borderTopRightRadius: 14 }} />
      <div style={{ fontFamily: font.body, fontWeight: 600, fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, marginBottom: 6 }}>{subtitle}</div>
      <h3 style={{ fontFamily: font.display, fontStyle: 'italic', fontWeight: 600, fontSize: 26, color: IVORY, margin: '0 0 22px' }}>{title}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {rows.map((r) => {
          const deltaColor = r.delta > 0 ? '#9be3aa' : r.delta < 0 ? '#ff9b8c' : MUTED;
          return (
            <div key={r.label} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, paddingBottom: 12, borderBottom: `1px solid rgba(197,162,74,0.10)` }}>
              <div style={{ fontFamily: font.body, fontWeight: 400, fontSize: 13, color: MUTED }}>{r.label}</div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: font.display, fontWeight: 600, fontSize: 20, color: IVORY }}>{r.val}</div>
                {typeof r.delta === 'number' && Number.isFinite(r.delta) && (
                  <div style={{ fontFamily: font.body, fontWeight: 600, fontSize: 11, color: deltaColor, marginTop: 2 }}>
                    {r.delta > 0 ? '▲' : r.delta < 0 ? '▼' : '—'} {Math.abs(r.delta)}pp
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModelColumn({ title, subtitle, data, deltaVs }) {
  const rows = [
    { label: 'Model',          val: data.model },
    { label: 'Completions',    val: data.completions },
    { label: 'Hot leads (Q7)', val: `${data.hot} · ${data.hot_pct}%`, delta: deltaVs?.hot_pct },
    { label: 'Warm leads',     val: `${data.warm} · ${data.warm_pct}%` },
    { label: 'Cold leads',     val: `${data.cold} · ${data.cold_pct}%` },
  ];
  return (
    <div style={{ background: CARD_BG, border: `1px solid ${LINE}`, borderRadius: 14, padding: '28px 28px 26px', flex: 1, minWidth: 280, position: 'relative', boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(to right, transparent 5%, ${GOLD_LT} 30%, ${GOLD} 50%, ${GOLD_LT} 70%, transparent 95%)`, borderTopLeftRadius: 14, borderTopRightRadius: 14 }} />
      <div style={{ fontFamily: font.body, fontWeight: 600, fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, marginBottom: 6 }}>{subtitle}</div>
      <h3 style={{ fontFamily: font.display, fontStyle: 'italic', fontWeight: 600, fontSize: 26, color: IVORY, margin: '0 0 22px' }}>{title}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {rows.map((r) => {
          const deltaColor = r.delta > 0 ? '#9be3aa' : r.delta < 0 ? '#ff9b8c' : MUTED;
          return (
            <div key={r.label} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, paddingBottom: 12, borderBottom: `1px solid rgba(197,162,74,0.10)` }}>
              <div style={{ fontFamily: font.body, fontWeight: 400, fontSize: 13, color: MUTED }}>{r.label}</div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: font.display, fontWeight: 600, fontSize: 20, color: IVORY }}>{r.val}</div>
                {typeof r.delta === 'number' && Number.isFinite(r.delta) && (
                  <div style={{ fontFamily: font.body, fontWeight: 600, fontSize: 11, color: deltaColor, marginTop: 2 }}>
                    {r.delta > 0 ? '▲' : r.delta < 0 ? '▼' : '—'} {Math.abs(r.delta)}pp
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminAbStats() {
  const [password, setPassword] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [entered, setEntered] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStats = async (pwd) => {
    if (!pwd) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/admin/ab-stats`, { params: { password: pwd }, timeout: 10000 });
      setData(res.data);
      localStorage.setItem(STORAGE_KEY, pwd);
    } catch (e) {
      if (e.response?.status === 401) {
        setError('Wrong password.');
        localStorage.removeItem(STORAGE_KEY);
        setPassword('');
      } else {
        setError('Could not load stats. ' + (e.message || ''));
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!password) return;
    fetchStats(password);
    const id = setInterval(() => fetchStats(password), 30000);
    return () => clearInterval(id);
  }, [password]);

  // Login screen
  if (!password || (error === 'Wrong password.' && !data)) {
    return (
      <div style={{ minHeight: '100vh', background: INK, color: IVORY, fontFamily: font.body, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <form onSubmit={(e) => { e.preventDefault(); setPassword(entered); fetchStats(entered); }}
              style={{ background: CARD_BG, border: `1px solid ${LINE}`, borderRadius: 14, padding: '38px 32px', width: '100%', maxWidth: 380, boxShadow: '0 24px 60px rgba(0,0,0,0.5)', position: 'relative' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(to right, transparent, ${GOLD}, transparent)`, borderTopLeftRadius: 14, borderTopRightRadius: 14 }} />
          <div style={{ fontWeight: 600, fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD, marginBottom: 12 }}>Admin · A/B Stats</div>
          <h1 style={{ fontFamily: font.display, fontStyle: 'italic', fontWeight: 600, fontSize: 30, color: IVORY, margin: '0 0 22px' }}>Sign in</h1>
          <label style={{ display: 'block', fontWeight: 500, fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: GOLD, marginBottom: 8 }}>Password</label>
          <input type="password" value={entered} onChange={(e) => setEntered(e.target.value)} autoFocus
                 placeholder="Shared password" data-testid="admin-pwd-input"
                 style={{ width: '100%', background: 'rgba(7,13,9,0.6)', border: `1px solid ${LINE}`, borderRadius: 10, padding: '14px 16px', color: IVORY, fontFamily: font.body, fontSize: 16, outline: 'none', marginBottom: 16 }} />
          {error && <div style={{ color: '#ff9b8c', fontSize: 13, marginBottom: 14 }} data-testid="admin-pwd-error">{error}</div>}
          <button type="submit" data-testid="admin-pwd-submit" disabled={!entered}
                  style={{ width: '100%', background: `linear-gradient(105deg, #8a6828, ${GOLD} 40%, ${GOLD_LT} 55%, ${GOLD} 70%, #8a6828)`, backgroundSize: '220% 100%', color: '#1a1206', border: 'none', borderRadius: 4, padding: '15px 24px', fontWeight: 700, fontSize: 13, letterSpacing: '0.2em', textTransform: 'uppercase', cursor: entered ? 'pointer' : 'not-allowed', opacity: entered ? 1 : 0.5, boxShadow: `0 10px 30px rgba(197,162,74,0.28)` }}>
            View stats →
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: INK, color: IVORY, fontFamily: font.body, padding: '32px 24px 60px' }} data-testid="admin-ab-dashboard">
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 30 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD }}>Admin · A/B Test Dashboard</div>
            <h1 style={{ fontFamily: font.display, fontStyle: 'italic', fontWeight: 600, fontSize: 38, color: IVORY, margin: '10px 0 4px' }}>Find Your <span style={{ color: GOLD_LT }}>ONLY</span> — Quiz Performance</h1>
            <div style={{ color: FAINT, fontSize: 12 }}>
              {loading ? 'Refreshing…' : data ? `Updated ${new Date(data.generated_at).toLocaleTimeString()} · auto-refresh 30s` : ''}
              {data && data.ab_started_at && (
                <span style={{ marginLeft: 10, color: GOLD }}>· Fair window since {new Date(data.ab_started_at).toLocaleString()}</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => fetchStats(password)} data-testid="admin-refresh"
                    style={{ background: 'transparent', color: GOLD_LT, border: `1px solid ${LINE}`, borderRadius: 4, padding: '10px 18px', fontWeight: 600, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer' }}>
              Refresh
            </button>
            <button onClick={() => { localStorage.removeItem(STORAGE_KEY); setPassword(''); setData(null); setEntered(''); }} data-testid="admin-logout"
                    style={{ background: 'transparent', color: MUTED, border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 4, padding: '10px 18px', fontWeight: 500, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', cursor: 'pointer' }}>
              Sign out
            </button>
          </div>
        </div>

        {!data && !error && <div style={{ color: MUTED }}>Loading…</div>}
        {error && data && <div style={{ color: '#ff9b8c', marginBottom: 20 }}>{error}</div>}

        {data && (
          <>
            {/* Headline stats */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
              <StatCard label="Total submissions" value={data.v1.completions + data.v2.completions} sub="Across both versions" />
              <StatCard label="V2 hot-lead lift" value={`${data.hot_pct_delta > 0 ? '+' : ''}${data.hot_pct_delta}pp`} sub="Hot % delta (v2 − v1)" delta={data.hot_pct_delta} />
              <StatCard label="V2 completion lift" value={data.completion_rate_delta == null ? 'n/a' : `${data.completion_rate_delta > 0 ? '+' : ''}${data.completion_rate_delta}pp`} sub={data.completion_rate_delta == null ? 'Awaiting comparable v1 data' : 'Completion rate delta'} delta={data.completion_rate_delta ?? undefined} />
            </div>

            {/* Per-version columns */}
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
              <VersionColumn title="Original Quiz" subtitle="Version 1 · Control" data={data.v1} />
              <VersionColumn title="Redesigned Quiz" subtitle="Version 2 · Variant" data={data.v2}
                             deltaVs={{ completion_rate: data.completion_rate_delta, hot_pct: data.hot_pct_delta }} />
            </div>

            {/* Model A/B section */}
            {data.model_ab && (data.model_ab.legacy.completions + data.model_ab.new.completions > 0) && (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginTop: 44, marginBottom: 18 }} data-testid="model-ab-section">
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD }}>Model A/B</div>
                    <h2 style={{ fontFamily: font.display, fontStyle: 'italic', fontWeight: 600, fontSize: 30, color: IVORY, margin: '8px 0 0' }}>
                      {data.model_ab.legacy.model.split('/').pop()} <span style={{ color: MUTED, fontStyle: 'normal' }}>vs</span> {data.model_ab.new.model.split('/').pop()}
                    </h2>
                  </div>
                  <div style={{ color: FAINT, fontSize: 12 }}>
                    {data.model_ab.started_at ? `Since ${new Date(data.model_ab.started_at).toLocaleString()}` : ''}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
                  <StatCard label="Total model completions" value={data.model_ab.legacy.completions + data.model_ab.new.completions} sub="Across both models" />
                  <StatCard
                    label={`${data.model_ab.new.model.split('/').pop()} hot-lead lift`}
                    value={`${data.model_ab.hot_pct_delta > 0 ? '+' : ''}${data.model_ab.hot_pct_delta}pp`}
                    sub={`Hot % delta (${data.model_ab.new.model.split('/').pop()} − ${data.model_ab.legacy.model.split('/').pop()})`}
                    delta={data.model_ab.hot_pct_delta}
                  />
                </div>

                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  <ModelColumn title="Legacy Model" subtitle="Control" data={data.model_ab.legacy} />
                  <ModelColumn title="New Model" subtitle="Variant" data={data.model_ab.new}
                               deltaVs={{ hot_pct: data.model_ab.hot_pct_delta }} />
                </div>
              </>
            )}

            <div style={{ fontFamily: font.body, fontSize: 11, color: FAINT, marginTop: 32, textAlign: 'center', letterSpacing: '0.08em', lineHeight: 1.7 }}>
              Quiz A/B: both versions counted from the same start point (the first v2 opt-in), so completion rates are directly comparable.<br />
              Model A/B: only generations tagged with a gen_model value are included, so both arms live in the same time window by construction.<br />
              Opt-ins are counted from user_sessions ∪ generations (unique emails). Hot / Warm / Cold percentages are relative to completions within each arm.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
